type Save = () => Promise<void>

export interface SaveQueue {
  schedule: (key: string, save: Save) => void
  flush: () => Promise<void>
}

/**
 * Keeps at most one write per key in flight and runs only the latest one that queued up behind it,
 * so a slow save cannot race the next one into the database and land out of order.
 */
export function useSaveQueue(): SaveQueue {
  const queued = new Map<string, Save>()
  const running = new Map<string, Promise<void>>()

  function drain(key: string): Promise<void> {
    const active = running.get(key)

    if (active) {
      return active
    }

    const run = Promise.resolve().then(async () => {
      try {
        for (let save = queued.get(key); save; save = queued.get(key)) {
          queued.delete(key)

          await save()
        }
      } finally {
        running.delete(key)
      }
    })

    running.set(key, run)

    return run
  }

  function schedule(key: string, save: Save): void {
    queued.set(key, save)

    void drain(key)
  }

  async function flush(): Promise<void> {
    const keys = new Set([...queued.keys(), ...running.keys()])

    await Promise.all([...keys].map(drain))
  }

  return { schedule, flush }
}
